/**
 * Diagnostics provider — converts workspace FileDiagnostics to LSP Diagnostics.
 */

import {
  DiagnosticSeverity,
  type Diagnostic,
} from "vscode-languageserver";
import { diagnosticText, suppressSymptoms, supportedFixes, type RepairFix } from "@archlang/engine";
import type { FileDiagnostic } from "../workspace/workspace.js";
import { toLspRange } from "../lsp-types.js";

/**
 * What rides in an LSP diagnostic's `data` — the machine half of a repair
 * receipt, round-tripped back to us on `textDocument/codeAction`.
 *
 * The transport used to drop `msgKey`/`params` at this boundary, which left a
 * code-action re-parsing the rendered English to recover a name the emit site
 * already had as structured data. LSP defines `data` as an opaque value
 * preserved across the request pair, so it is exactly the channel for it.
 *
 * The structured half is DERIVED from `FileDiagnostic` rather than re-listed,
 * so a field added at the emit site cannot be silently dropped here again (as
 * `evidence` was, one field after `msgKey`/`params`).
 */
export type ArchDiagnosticData = Pick<FileDiagnostic, "msgKey" | "params" | "evidence"> & {
  /** Repairs the registry binds to this code — a client may offer them. */
  readonly supportedFixes?: readonly RepairFix[];
};

/** The `FileDiagnostic` fields that ride the wire, in one place: `dataOf` copies
 *  exactly these, so extending the payload type extends the transport. */
const CARRIED = ["msgKey", "params", "evidence"] as const;

/**
 * Convert engine diagnostics to LSP diagnostics, rendering each message in the
 * editor's `locale` (from InitializeParams) via the shared catalog — falling
 * back to the baked English `message` for un-catalogued diagnostics. Symptom
 * diagnostics whose root cause is in the same file are dropped, so the editor
 * squiggles the cause, not the wreckage.
 */
export function toLspDiagnostics(
  diags: readonly FileDiagnostic[],
  locale?: string,
): Diagnostic[] {
  return suppressSymptoms(diags).map((d) => ({
    severity: severityOf(d.severity),
    message: diagnosticText(d, locale),
    source: "archlang",
    code: d.code,
    range: d.span
      ? toLspRange(d.span)
      : { start: { line: 0, character: 0 }, end: { line: 0, character: 0 } },
    ...dataOf(d),
    // Secondary locations (e.g. jump to the policy rule behind a violation).
    // Only entries with a target file become clickable locations.
    ...(d.relatedInformation && d.relatedInformation.some((r) => r.span.file)
      ? {
          relatedInformation: d.relatedInformation
            .filter((r) => r.span.file)
            .map((r) => ({
              location: { uri: r.span.file!, range: toLspRange(r.span) },
              message: r.message,
            })),
        }
      : {}),
  }));
}

/** The `data` payload, omitted entirely when there is nothing to carry. */
function dataOf(d: FileDiagnostic): { data?: ArchDiagnosticData } {
  const fixes = supportedFixes(d.code);
  const carried = Object.fromEntries(
    CARRIED.filter((k) => d[k] !== undefined).map((k) => [k, d[k]]),
  );
  if (Object.keys(carried).length === 0 && fixes.length === 0) return {};
  const data: ArchDiagnosticData = {
    ...carried,
    ...(fixes.length > 0 ? { supportedFixes: fixes } : {}),
  };
  return { data };
}

/** Read our own `data` back off a diagnostic the client round-tripped. */
export function archDiagnosticData(d: Diagnostic): ArchDiagnosticData | undefined {
  const data = d.data as ArchDiagnosticData | undefined;
  return data && typeof data === "object" ? data : undefined;
}

/** A diagnostic's did-you-mean candidates — structured, never re-parsed out of
 *  the rendered message. */
export function didYouMeanOf(d: Diagnostic): string[] {
  const cands = archDiagnosticData(d)?.params?.["didYouMean"];
  return Array.isArray(cands) ? cands.map((c) => String(c)) : [];
}

/** A param the emit site recorded, by name. */
export function diagnosticParam(d: Diagnostic, name: string): string | undefined {
  const v = archDiagnosticData(d)?.params?.[name];
  return typeof v === "string" ? v : undefined;
}

function severityOf(s: FileDiagnostic["severity"]): DiagnosticSeverity {
  switch (s) {
    case "error":
      return DiagnosticSeverity.Error;
    case "warning":
      return DiagnosticSeverity.Warning;
    case "todo":
      // Draft debt (§4.13): visible but non-alarming — surfaced as info, not a
      // red/yellow squiggle. Tooling counts these as outstanding TODOs.
      return DiagnosticSeverity.Information;
    case "info":
      return DiagnosticSeverity.Information;
    case "hint":
      return DiagnosticSeverity.Hint;
  }
}
